import { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { useNavigate, useParams } from "react-router-dom";
import { IRecipeRecord } from "../../lib/types";
import pb from "../../lib/pocketbase";
import { Skeleton } from "../../components/ui/skeleton";
import { config } from "../../config";
import Container from "../../components/Container";
import ModeSwitch from "../../components/ModeSwitch";
import Markdown, { MarkdownStyle } from "../../components/markdown/Markdown";
import Checkbox from "../../components/form/checkbox";
import { RecipeTags } from "../../lib/consts";

interface IIngredient {
    ingredient: string;
}

export default function PublicRecipe() {
    const navigate = useNavigate();
    const id = useParams().id;
    const [loading, setLoading] = useState(true);
    const [recipe, setRecipe] = useState<IRecipeRecord>();

    async function load() {
        if (!id) {
            navigate("/404");
            return;
        }
        try {
            const record: IRecipeRecord = await pb.collection("recipe").getOne(id, {
                expand: "ingredients"
            });
            setRecipe(record);
            setLoading(false);
        } catch (e) {
            navigate("/404");
        }
    }

    useEffect(() => {
        load();
    }, []);

    const image = config.pocketbase + "/api/files/" + recipe?.collectionId + "/" + recipe?.id + "/" + recipe?.image;

    if (loading)
        return (
            <div>
                <Skeleton className="hidden h-64 w-full md:block" />
                <Container className="my-8 @container md:my-8 lg:my-16">
                    <div className="flex items-center gap-2">
                        <Skeleton className="h-6 w-12 rounded" />
                        <Skeleton className="h-6 w-14 rounded" />
                    </div>
                    <Skeleton className="mt-4 h-8 w-72 rounded" />
                    <Skeleton className="mt-1 h-4 w-48 rounded" />
                    <Skeleton className="mt-1 h-4 w-40 rounded" />
                    <Skeleton className="mt-16 h-8 w-64 rounded" />
                    <div className="mt-4 flex flex-col gap-2">
                        <div className="flex items-center gap-2">
                            <Skeleton className="h-6 w-6 rounded" />
                            <Skeleton className="h-6 w-32 rounded" />
                        </div>
                        <div className="flex items-center gap-2">
                            <Skeleton className="h-6 w-6 rounded" />
                            <Skeleton className="h-6 w-32 rounded" />
                        </div>
                        <div className="flex items-center gap-2">
                            <Skeleton className="h-6 w-6 rounded" />
                            <Skeleton className="h-6 w-32 rounded" />
                        </div>
                    </div>
                </Container>
            </div>
        );

    return (
        <>
            <Helmet>
                <title>ReinVB — Recipe — {recipe?.title}</title>
            </Helmet>
            <div className="relative z-10 mb-12 md:mb-0">
                <img src={image} alt={recipe?.title} className="hidden h-64 w-full object-cover md:block" />
                <ModeSwitch className="absolute right-2 top-2 rounded bg-white px-2 py-2 dark:bg-slate-900" />
            </div>

            <Container className="my-8 @container md:my-8 lg:my-16">
                <MarkdownStyle>
                    <div className="flex items-center gap-2">
                        {recipe?.tags.map((tag) => {
                            return <div className="w-fit rounded bg-slate-200 px-2 py-1 text-sm dark:bg-slate-700">{RecipeTags[tag]}</div>;
                        })}
                    </div>
                    <h1 className="mb-0 mt-4">{recipe?.title}</h1>
                    {recipe?.duration && <div className="italic text-slate-400 dark:text-slate-500">Duration: {recipe.duration}</div>}
                    <div>Servings: {recipe?.servings}</div>
                    <div>
                        <h2>Ingredients:</h2>
                        <div className="mt-4 flex flex-col gap-2">
                            {recipe?.expand.ingredients?.map((ingredient: IIngredient) => {
                                return <Checkbox checked={false} onClick={() => {}} label={ingredient.ingredient} />;
                            })}
                        </div>
                    </div>
                    <div className="mt-8">{recipe?.recipe && <Markdown>{recipe.recipe}</Markdown>}</div>
                </MarkdownStyle>
            </Container>
        </>
    );
}
